import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ENV } from '@shared/config/env'
import { classifyAuthError, parse429 } from '@shared/api/httpErrors'
import { getProfile, updateProfileName, useInvalidateProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import { sendCode, resendCode, verifyCode } from '../api/webAuthApi'
import { useAuthFlowStore } from './useAuthFlowStore'
import { saveNextResend, formatResendWaitMessage } from './useResendTimer'

interface UseAuthFlowParams {
  redirectTo: string
}

interface UseAuthFlowReturn {
  state: ReturnType<typeof useAuthFlowStore.getState>
  submitting: boolean
  shakeSignal: number
  debugCode: string | null

  handleEmailSubmit: (email: string) => Promise<void>
  handleResend: () => Promise<void>
  handleVerify: (code: string) => Promise<void>
  handleNameSubmit: (name: string) => Promise<void>
  handleOfferTelegramNext: () => Promise<void>
  changeEmail: () => void
}

/**
 * Инкапсулирует бизнес-логику флоу веб-авторизации.
 * @param params.redirectTo - URL для редиректа после успеха
 * @returns Обработчики шагов и состояние флоу
 */
export const useAuthFlow = ({ redirectTo }: UseAuthFlowParams): UseAuthFlowReturn => {
  const navigate = useNavigate()
  const store = useAuthFlowStore()
  const {
    email,
    setStep, setEmail, setCooldown, setError, setAttemptsLeft, setLocked, setDebugCode, reset
  } = store
  const setProfile = useAppStore((s) => s.setProfile)
  const invalidateProfile = useInvalidateProfile()
  const [submitting, setSubmitting] = useState(false)
  const [shakeSignal, setShakeSignal] = useState(0)

  const parseAttemptsLeft = (err: unknown): number | null => {
    const detail = (err as { response?: { data?: { detail?: unknown } } } | undefined)?.response?.data?.detail
    if (typeof detail !== 'string') return null
    const match = /попыток:\s*(\d+)/i.exec(detail)
    return match ? Number(match[1]) : null
  }

  const handleEmailSubmit = async (value: string) => {
    setSubmitting(true); setError(null)
    try {
      const res = await sendCode(value)
      setEmail(value)
      setCooldown(res.next_resend, res.expired_at)
      saveNextResend(res.next_resend)
      if (ENV.IS_DEV && res.code) setDebugCode(res.code)
      setStep('code')
    } catch (err) {
      const cooldown = parse429(err)
      if (cooldown) {
        setEmail(value)
        setCooldown(cooldown.next_resend, cooldown.expired_at)
        saveNextResend(cooldown.next_resend)
        if (ENV.IS_DEV && cooldown.code) setDebugCode(cooldown.code)
        setStep('code')
        setError(formatResendWaitMessage(cooldown.next_resend) ?? cooldown.message)
        return
      }
      const kind = classifyAuthError(err)
      if (kind === 'already_authenticated') {
        navigate(redirectTo, { replace: true })
        return
      }
      setError(kind === 'rate_limit'
        ? 'Слишком много запросов. Попробуйте позже.'
        : 'Не удалось отправить код')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!email) return
    setSubmitting(true); setError(null)
    setLocked(false); setAttemptsLeft(null)
    try {
      const res = await resendCode(email)
      setCooldown(res.next_resend, res.expired_at)
      saveNextResend(res.next_resend)
      if (ENV.IS_DEV && res.code) setDebugCode(res.code)
    } catch (err) {
      const cooldown = parse429(err)
      if (cooldown) {
        setCooldown(cooldown.next_resend, cooldown.expired_at)
        saveNextResend(cooldown.next_resend)
        setError(formatResendWaitMessage(cooldown.next_resend) ?? cooldown.message)
        return
      }
      const kind = classifyAuthError(err)
      if (kind === 'code_not_found') {
        const res = await sendCode(email)
        setCooldown(res.next_resend, res.expired_at)
        saveNextResend(res.next_resend)
        if (ENV.IS_DEV && res.code) setDebugCode(res.code)
      } else {
        setError('Не удалось отправить код')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerify = async (code: string) => {
    if (!email) return
    setSubmitting(true); setError(null)

    let res: Awaited<ReturnType<typeof verifyCode>>
    try {
      res = await verifyCode(email, code)
    } catch (err) {
      const kind = classifyAuthError(err)
      switch (kind) {
        case 'code_invalid':
          setShakeSignal((x) => x + 1)
          setAttemptsLeft(parseAttemptsLeft(err))
          setError('Неверный код.')
          break
        case 'code_expired':
          setError('Срок действия кода истёк. Запросите новый.')
          setLocked(true)
          break
        case 'code_not_found':
          setError('Код не найден. Запросите новый.')
          setLocked(true)
          break
        case 'max_attempts':
          setLocked(true)
          setError('Код недействителен. Запросите новый код.')
          break
        case 'rate_limit':
          setLocked(true)
          setError('Слишком много попыток. Попробуйте позже.')
          break
        case 'already_authenticated':
          navigate(redirectTo, { replace: true })
          return
        default:
          setError('Не удалось проверить код')
      }
      setSubmitting(false)
      return
    }

    saveNextResend(null)
    let profile = null as Awaited<ReturnType<typeof getProfile>> | null
    try {
      profile = await getProfile()
      setProfile(profile)
      await invalidateProfile()
    } catch (err) {
      const anyErr = err as { response?: { status?: number } }
      setError(`Код принят, но /user/profile вернул ${anyErr?.response?.status ?? '?'}`)
      setSubmitting(false)
      return
    }

    const needsTelegram = res.is_new_user && profile.telegram === null
    const needsName = profile.name === ''

    if (needsTelegram) {
      setStep('offer-telegram')
    } else if (needsName) {
      setStep('name')
    } else {
      reset()
      navigate(redirectTo, { replace: true })
    }
    setSubmitting(false)
  }

  const handleOfferTelegramNext = async () => {
    const profile = await getProfile().catch(() => null)
    if (profile) setProfile(profile)
    if (profile && profile.name === '') {
      setStep('name')
    } else {
      reset()
      navigate(redirectTo, { replace: true })
    }
  }

  const handleNameSubmit = async (name: string) => {
    setSubmitting(true); setError(null)
    try {
      const profile = await updateProfileName(name)
      setProfile(profile)
      await invalidateProfile()
      reset()
      navigate(redirectTo, { replace: true })
    } catch {
      setError('Не удалось сохранить имя')
    } finally {
      setSubmitting(false)
    }
  }

  const changeEmail = () => {
    setError(null)
    setLocked(false)
    setAttemptsLeft(null)
    setStep('email')
  }

  return {
    state: store,
    submitting,
    shakeSignal,
    debugCode: store.debugCode,
    handleEmailSubmit,
    handleResend,
    handleVerify,
    handleNameSubmit,
    handleOfferTelegramNext,
    changeEmail
  }
}
